<template>
  <div class="flex justify-center items-center w-inherit -mt-10 pb-8 relative z-10">
    <div class="flex flex-col justify-center items-center w-full md:mx-2">
      <transition name="fade" mode="out-in">
        <div class="w-full" v-if="loading">
          <channel-thread-loading-item></channel-thread-loading-item>
          <channel-thread-loading-item></channel-thread-loading-item>
          <channel-thread-loading-item></channel-thread-loading-item>
          <channel-thread-loading-item></channel-thread-loading-item>
          <channel-thread-loading-item></channel-thread-loading-item>
          <channel-thread-loading-item></channel-thread-loading-item>
          <channel-thread-loading-item></channel-thread-loading-item>
          <channel-thread-loading-item></channel-thread-loading-item>
          <channel-thread-loading-item></channel-thread-loading-item>
          <channel-thread-loading-item></channel-thread-loading-item>
        </div>
        <div class="w-full" v-else>
          <div class="w-full" v-if="threads.data.length !== 0">
            <channel-thread-item
              :key="thread.slug"
              v-for="thread in threads.data"
              :thread="thread"
            />

            <pagination :data="threads" route="channels.show"></pagination>
          </div>

          <div
            class="w-full mt-2 mb-6 px-4 py-6 text-grey-8 border border-grey-3 bg-grey-1 rounded text-center"
            v-else
          >
            There are no threads in this channel.
            <span
              v-if="$auth.check()"
            >Be the first to create a thread using the button above!</span>
          </div>
        </div>
      </transition>
    </div>

    <portal to="banner-portal">
      <div class="w-full relative">
        <img
          v-if="channel && channel.image"
          :srcset="`${channel.image[600]} 600w, ${channel.image[800]} 800w, ${channel.image.full} 1200w`"
          sizes="(max-width: 600px) 600px,
            (max-width: 800px) 800px,
            1200px"
          :src="channel.image.full"
          class="w-full h-full object-cover absolute z-0 md:rounded-l-lg"
        />
        <img
          v-else
          srcset="@/assets/channel_background-600w.png 600w, @/assets/channel_background-800w.png 800w, @/assets/channel_background-1200w.png 1200w"
          sizes="(max-width: 600px) 600px,
            (max-width: 800px) 800px,
            1200px"
          src="@/assets/channel_background-1200w.png"
          class="w-full h-full object-cover absolute z-0 md:rounded-l-lg"
        />
        <div class="w-full h-full absolute z-10 bg-grey-1 opacity-75 md:rounded-l-lg"></div>
        <div
          class="w-full h-full absolute z-20 flex flex-col items-center justify-center pt-20"
          v-if="channel"
        >
          <h2 class="text-xl text-yellow-8 font-semibold tracking-wider mb-1">{{ channel.name }}</h2>
          <div class="w-full text-center text-sm uppercase opacity-75 text-grey-7">
            <span class="inline-block font-semibold">Threads</span>
            <span class="inline-block text-blue-7 font-bold ml-1">{{ channel.thread_count }}</span>
            <span class="inline-block font-semibold ml-2">Replies</span>
            <span class="inline-block text-blue-6 font-bold ml-1">{{ channel.reply_count }}</span>
            <span class="font-semibold ml-0 md:ml-2" v-if="channel.latest_post">Last Post</span>
            <span
              class="text-yellow-7 font-bold ml-1"
              v-if="channel.latest_post"
            >{{ fromNow(channel.latest_post) }}</span>
          </div>
          <div v-if="$auth.check()" class="w-full mt-2">
            <div class="block text-center">
              <nx-button
                class="mx-1"
                type="primary"
                v-if="!channel.locked || authCanModify"
                @click="$router.push({name: 'threads.create', params: {channel: channel.slug}})"
              >
                <plus-icon slot="icon"></plus-icon>
                <span>New Thread</span>
              </nx-button>
              <nx-button class="mx-1" type="secondary" v-if="channel.new" @click="markRead">
                <eye-icon slot="icon"></eye-icon>
                <span>Mark All Threads Read</span>
              </nx-button>
            </div>
          </div>
        </div>
      </div>
    </portal>

    <portal to="breadcrumb-portal">
      <breadcrumbs v-if="channel && currentCategory">
        <router-link class="breadcrumb-link" :to="{name: 'channels.index'}">Home</router-link>
        <chevron-right-icon class="mx-1"></chevron-right-icon>
        <span class="breadcrumb-item">{{ currentCategory.name }}</span>
        <chevron-right-icon class="mx-1"></chevron-right-icon>
        <span class="breadcrumb-current">{{ channel.name }}</span>
      </breadcrumbs>
    </portal>

    <portal to="modal-portal">
      <thread-create-form
        v-if="newThreadForm"
        @close="newThreadForm = false"
        @success="success('create')"
      />
    </portal>
  </div>
</template>

<script>
import ChannelThreadItem from "@/components/Items/ChannelThreadItem"
import ChannelThreadLoadingItem from "@/components/Items/Loading/ChannelThreadLoadingItem"
import ThreadCreateForm from "@/components/Forms/ThreadCreateForm"
import Pagination from "@/components/Pagination"
import { EventBus } from "@/events"
import DateTime from "@/mixins/DateTime"
import BreadcrumbHelper from "@/mixins/BreadcrumbHelper"
import { PlusIcon, EyeIcon, ChevronRightIcon } from "vue-feather-icons"

export default {
  name: "Channel",
  mixins: [BreadcrumbHelper, DateTime],
  components: {
    ChannelThreadItem,
    ChannelThreadLoadingItem,
    ThreadCreateForm,
    Pagination,
    PlusIcon,
    EyeIcon,
    ChevronRightIcon
  },
  data() {
    return {
      newThreadForm: false,
      channel: null,
      page: 1,
      threads: null
    }
  },
  computed: {
    authCanModify() {
      if (this.$auth.check(["admin", "super-moderator"])) return true
      if (
        this.$auth.check(["moderator"]) &&
        this.$auth
          .user()
          .moderatable_channels.indexOf(this.$route.params.channel) !== -1
      )
        return true

      return false
    },
    loading() {
      return this.channel === null || this.threads === null
    },
    lastPost() {
      if(!this.channel.latest_reply && !this.channel.latest_thread) return null
      if(!this.channel.latest_reply && this.channel.latest_thread) return this.channel.latest_thread.created_at
      return this.latest(this.channel.latest_reply.created_at, this.channel.latest_thread.created_at)
    }
  },
  watch: {
    $route: {
      handler() {
        if (this.$route.query.page !== null) this.page = this.$route.query.page
        this.fetch()
      },
      immediate: true
    },
    channel: {
      handler(val) {
        if (val) {
          document.title = val.name + " - Nexus - The Pylon Show"
        }
      },
      immediate: true
    }
  },
  methods: {
    async fetch() {
      this.threads = this.channel = null
      await this.$api.channels
        .show(this.$route.category, this.$route.channel)
        .then(({ data }) => (this.channel = data))
      await this.$api.threads
        .get(this.page)
        .then(({ data }) => (this.threads = data))
    },
    async markRead() {
      await this.$api.channels
        .read()
        .then(() => this.$toasted.success("All threads marked as read."))
      await this.fetch()
    },
    createdAt(time) {
      time = this.$moment.tz(time, "UTC")
      if (this.$auth.check())
        return time
          .tz(this.$auth.user().timezone)
          .format("MMM Do YYYY [at] h:mm A")
      else
        return (
          time.tz("America/New_York").format("MMM Do YYYY [at] h:mm A") + " EST"
        )
    }
  },
  mounted() {
    EventBus.$on("auth:login", () => this.fetch())
    EventBus.$on("auth:logout", () => this.fetch())
  },
  beforeDestroy() {
    EventBus.$off("auth:login")
    EventBus.$off("auth:logout")
  }
}
</script>
